import Lenis from "lenis";
import { useReducedMotion } from "motion/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { gsap, ScrollTrigger } from "../lib/gsap";

type ScrollTarget = number | string | HTMLElement;

interface ScrollOptions {
  immediate?: boolean;
  offset?: number;
}

interface SmoothScrollValue {
  scrollTo: (target: ScrollTarget, options?: ScrollOptions) => void;
  /** Trava a rolagem da página (modais/drawer). Retorna a função que destrava. */
  lockScroll: () => () => void;
}

const SmoothScrollContext = createContext<SmoothScrollValue | null>(null);

/** Altura da navbar fixa, para as âncoras não ficarem escondidas atrás dela. */
export const NAV_OFFSET = -88;

export const SmoothScrollProvider = ({ children }: { children: ReactNode }) => {
  const lenisRef = useRef<Lenis | null>(null);
  const locks = useRef(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    // Com prefers-reduced-motion o Lenis nem é criado: fica a rolagem nativa.
    if (reducedMotion) return;

    const lenis = new Lenis({ autoRaf: false, lerp: 0.1 });
    lenisRef.current = lenis;
    if (locks.current > 0) lenis.stop();

    // Lenis e ScrollTrigger no mesmo relógio (ticker do GSAP).
    lenis.on("scroll", ScrollTrigger.update);
    const tick = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tick);
      gsap.ticker.lagSmoothing(500, 33);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [reducedMotion]);

  const scrollTo = useCallback(
    (target: ScrollTarget, { immediate = false, offset = 0 }: ScrollOptions = {}) => {
      const lenis = lenisRef.current;
      if (lenis) {
        lenis.scrollTo(target, { offset, immediate, force: true });
        return;
      }

      let top: number;
      if (typeof target === "number") {
        top = target;
      } else {
        const el = typeof target === "string" ? document.querySelector(target) : target;
        if (!el) return;
        top = el.getBoundingClientRect().top + window.scrollY;
      }
      window.scrollTo({
        top: top + offset,
        behavior: immediate || reducedMotion ? "auto" : "smooth",
      });
    },
    [reducedMotion],
  );

  const lockScroll = useCallback(() => {
    locks.current += 1;
    if (locks.current === 1) {
      lenisRef.current?.stop();
      document.documentElement.style.overflow = "hidden";
    }

    let released = false;
    return () => {
      if (released) return;
      released = true;
      locks.current -= 1;
      if (locks.current === 0) {
        lenisRef.current?.start();
        document.documentElement.style.overflow = "";
      }
    };
  }, []);

  const value = useMemo(() => ({ scrollTo, lockScroll }), [scrollTo, lockScroll]);

  return (
    <SmoothScrollContext.Provider value={value}>{children}</SmoothScrollContext.Provider>
  );
};

export const useSmoothScroll = () => {
  const ctx = useContext(SmoothScrollContext);
  if (!ctx) throw new Error("useSmoothScroll precisa estar dentro de <SmoothScrollProvider>");
  return ctx;
};

/** Trava a rolagem enquanto `active` for true. */
export const useScrollLock = (active: boolean) => {
  const { lockScroll } = useSmoothScroll();
  useEffect(() => {
    if (!active) return;
    return lockScroll();
  }, [active, lockScroll]);
};
